/**
 * Cross-platform utility to copy text to clipboard
 * Uses a special approach for iOS Safari
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  // Check if running on iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  
  // First try the modern Clipboard API if not on iOS
  if (!isIOS && navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed:', err);
    }
  }

  // iOS-specific approach
  try {
    // Create temporary input element
    const textArea = document.createElement('input');
    textArea.value = text;
    textArea.type = 'text';
    
    // Style for iOS
    textArea.style.position = 'fixed';
    textArea.style.left = '0';
    textArea.style.top = '0';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    textArea.style.zIndex = '-1';

    // Required for iOS
    textArea.setAttribute('readonly', 'readonly');
    textArea.setAttribute('contenteditable', 'true');
    
    document.body.appendChild(textArea);

    // Select the text
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, 99999);

    // For iOS Safari
    if (isIOS) {
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textArea.setSelectionRange(0, 99999);
    }

    // Execute copy command
    const successful = document.execCommand('copy');
    
    // Cleanup after a short delay to ensure copy completes
    setTimeout(() => {
      document.body.removeChild(textArea);
    }, 100);
    
    return successful;
  } catch (err) {
    console.error('Fallback clipboard method failed:', err);
    return false;
  }
}
